import React, { useRef, useState } from 'react';
import introVideo from '../assets/intro-video.mp4';

const NarrativeSection = ({ onGetStarted }) => {
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);

  const toggleVideoPlayback = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      {/* Full-screen background video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src={introVideo} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Video overlay with content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center items-center text-center
        bg-black bg-opacity-50 text-white p-6">

        {/* Video Controls */}
        <div className="absolute top-6 right-6 z-20">
          <button
            onClick={toggleVideoPlayback}
            className="bg-white bg-opacity-20 hover:bg-opacity-40 text-white
              rounded-full p-2 transition-all duration-300"
            aria-label={isVideoPlaying ? "Pause Video" : "Play Video"}
          >
            {isVideoPlaying ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </button>
        </div>

        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6 drop-shadow-lg">
            Navigating the Path to Net Zero
          </h1>

          <div className="bg-white bg-opacity-20 rounded-lg p-6 mb-8 backdrop-blur-sm">
            <p className="text-xl mb-4">
             The UK has committed to reaching net zero emissions by 2050. This dashboard shows different ways that might be achieved.
            </p>
            <p className="text-lg">
              Individual and collective decisions can shape our sustainable future.
            </p>
            </div>
          <div className='mb-6'>
            <h2 className='text-xl font-bold'>We will use scenarios to explore these topics and how we can shape the energy future of the UK. All figures assume that the UK population grows in line with the Office for National Statistics' latest data</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                title: "Reducing energy demand",
                description: "How we shape the future by different technology choices and new ways of doing things to use less energy.",
                color: "text-blue-300"
              },
              {
                title: "Cleaning up our electricity supply",
                description: "How much new infrastructure we need to build to provide clean electricity (e.g. nuclear power plants, wind farms, pylons)",
                color: "text-green-300"
              },
              {
                title: "Removing Emissions",
                description: "How much carbon dioxide we need to remove from the atmosphere, to balance out the emissions we still produce",
                color: "text-purple-300"
              }
            ].map(({ title, description, color }) => (
              <div
                key={title}
                className="bg-white bg-opacity-10 p-4 rounded-lg backdrop-blur-sm border border-white border-opacity-20"
              >
                <h3 className={`text-lg font-semibold mb-2 ${color}`}>{title}</h3>
                <p className="text-sm text-gray-100">{description}</p>
              </div>
            ))}
          </div>

          {/* Get Started Button */}
          <button
            onClick={onGetStarted}
            className="mt-6 px-8 py-3 bg-blue-600 text-white rounded-lg
              hover:bg-blue-700 transition-colors duration-300
              flex items-center justify-center mx-auto space-x-2
              shadow-md hover:shadow-lg"
          >
            <span>Get Started</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NarrativeSection;
